import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { imageModelLabel } from '@inference.engine/widgets/web';
import type { ImageModel } from './ImageGenerationOptions.types';
import { isKnownImageModel, resolveStoredModel } from './ImageOptionsHelpers';

export interface ImageModelChipProps {
  models: readonly string[];
  value: string;
  onChange: (model: string) => void;
  disabled?: boolean;
}

/** The image model beside the chat model, for an agent built with Generate image. */
export function ImageModelChip({ models, value, onChange, disabled }: ImageModelChipProps) {
  const { t } = useTranslation('chats');
  // A chat keeps the model last picked, so a retired one is shown as the model it now runs on.
  const current: string = resolveStoredModel(value as ImageModel);
  // The API can list a model before this build knows how to show its settings.
  const known = models.filter(isKnownImageModel);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 rounded-lg px-2 py-0.5 text-xs font-medium text-neutral-700"
          disabled={disabled || known.length === 0}
          aria-label={t('chats.chatInputV2.imageModelNamed', { model: imageModelLabel(current) })}
        >
          <span>{imageModelLabel(current)}</span>
          <ChevronDown className="ml-2 h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="p-2">{t('chats.chatInputV2.imageModel')}</DropdownMenuLabel>
        {known.map((model) => (
          <DropdownMenuItem
            key={model}
            onClick={() => {
              if (model !== current) onChange(model);
            }}
          >
            <div className="flex w-full items-center gap-2">
              <span className="flex-1">{imageModelLabel(model)}</span>
              {model === current && <Check className="h-4 w-4" />}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
